<template>
  <v-container fluid>
    <v-row>
      <v-col cols="auto">
        <v-icon size="large" icon="mdi-chart-timeline-variant" color="dark-cyan"></v-icon>
      </v-col>
      <v-col align-self="center">
        <h2 class="itemis-analyze-title"> {{ labels.CUSTOM_METRIC.CUSTOM_METRICS }} </h2>
      </v-col>
      <v-col cols="auto">
        <v-btn
          id="back-btn"
          color="gray"
          variant="flat"
          prepend-icon="mdi-arrow-left"
          class="secondary-btn"
          @click="cancel"
        >
          Back
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-form ref="form" fast-fail @submit.prevent>
        <div class="left-align-fields">
          <analyze-name-description
            :input-name="customMetricName"
            :input-name-id="`name-field-custom-metric-${currentCustomMetric?.id}`"
            :input-name-label="labels.CUSTOM_METRIC.CUSTOM_METRICS"
            :input-name-placeholder="labels.CUSTOM_METRIC.DEFAULT_METRIC_NAME"
            :input-description="currentCustomMetric ? currentCustomMetric.description : ''"
            :input-description-id="`description-field-custom-metric-${currentCustomMetric?.id}`"
            :input-description-label="labels.CUSTOM_METRIC.CUSTOM_METRICS_DESCRIPTION"
            :input-description-placeholder="labels.CUSTOM_METRIC.DEFAULT_METRIC_DESCRIPTION"
            :is-view-mode="false"
            @description-input="applyDescription"
            @name-input="applyMetricName"
          />
        </div>
      </v-form>
    </v-row>
    <v-row>
      <div id="custom-metrics-wrapper" class="full-width">
        <v-row no-gutters>
          <v-col cols="4" :order="isReversed ? 1 : 0">
            <custom-metric-tool-box
              class="fixed-toolbox"
              :toolbox="toolbox"
              :target-uuid="selectedTarget"
              @search-update="searchTextUpdate"
              @drag-start="dragStarted"
              @select-tool="selectTool($event)"
              @on-toggle-button-click="toggleLayout"
            ></custom-metric-tool-box>
          </v-col>
          <v-col cols="8">
            <custom-metric-table
              class="flexible-table"
              :disabled="false"
              :query="query"
              :current-context-uuids="currentToolUUIDs"
              :tools="toolbox.tools"
              @dropped-tool="droppedToolOnQuery"
              @item-selected="selectTargetUuid"
              @open-filter-menu="openFilterMenu"
            ></custom-metric-table>
          </v-col>
        </v-row>
      </div>
    </v-row>
    <v-row class="justify-center my-5">
      <v-btn class="secondary-btn action-btn" variant="flat" @click="cancel">
        {{ labels.CUSTOM_METRIC.CANCEL }}
      </v-btn>
      <v-btn
        type="submit"
        class="primary-btn action-btn ml-5"
        variant="flat"
        @click="saveCustomMetric"
      >
        {{ labels.CUSTOM_METRIC.SAVE }}
      </v-btn>
    </v-row>

    <custom-metric-filter-modal
      :is-opened="openCustomMetricFilterModal"
      :query="query"
      :tools="toolbox.tools || []"
      :is-view-mode="false"
      @target-selected="selectTargetUuid"
      @selected-tool="droppedToolOnQuery"
      @close-dialog="
        (filter) => {
          if (query) query.filter = filter
          openCustomMetricFilterModal = false
        }
      "
    />
  </v-container>
</template>

<script lang="ts" setup>
  import { ref, watch } from 'vue'
  import labels from '@/translations/labels.json'
  import { useProjectStore } from '@/stores/projectStore'
  import { storeToRefs } from 'pinia'
  import { VForm } from 'vuetify/lib/components/index.mjs'
  import { CustomMetric, Query, Tool, Toolbox } from '@/models/custom-metric.model'
  import { useCustomMetricStore } from '@/stores/customMetricStore'
  import CustomMetricToolBox from '@/components/CustomMetrics/CustomMetricToolBox.vue'
  import CustomMetricTable from '@/components/CustomMetrics/CustomMetricTable.vue'
  import AnalyzeNameDescription from '@/components/AnalyzeNameDescription/AnalyzeNameDescription.vue'
  import { EventBus } from '@/event-bus'
  import CustomMetricFilterModal from '@/components/CustomMetrics/CustomMetricFilterModal.vue'
  import { useRoute, useRouter } from 'vue-router'
  import { ServiceKeys } from '@/services/symbols'
  import { injectTyped } from '@/inject'
  import { computed } from 'vue'
  import {
    hasToolboxAssist,
    removeAllToolBoxAssistFieldsFromSelect,
    removeExtraToolboxAssistFields,
  } from '@/utils/custom-metric-filters'

  const router = useRouter()
  const route = useRoute()
  const customMetricService = injectTyped(ServiceKeys.customMetrics)

  EventBus.on('createNewCustomMetric', showCreateNewCustomMetric)
  EventBus.on('openCustomMetricToEdit', openCustomMetricToEdit)

  const customMetricStore = useCustomMetricStore()
  const projectStore = useProjectStore()

  const { currentProject } = storeToRefs(projectStore)
  const currentCustomMetric = ref<CustomMetric | undefined>()

  const customMetricName = ref<string>(currentCustomMetric.value?.name ?? '')
  const customMetricDescription = ref<string>(currentCustomMetric.value?.description ?? '')

  const form = ref<VForm | null>(null)

  const toolbox = ref<Toolbox>({})
  const selectedTarget = ref<string>()
  const searchText = ref<string>()
  const currentToolUUIDs = ref<string[]>([])

  const query = computed(() => toolbox.value.query) //ref<Query | undefined>(currentCustomMetric.value?.query)

  const isReversed = ref(false)

  const openCustomMetricFilterModal = ref(false)

  watch([currentProject, searchText], async () => {
    updateToolbox()
  })

  watch(() => route.params.customMetricId, refreshCurrentMetric)

  function selectTargetUuid(uuid?: string) {
    selectedTarget.value = uuid
    currentToolUUIDs.value = uuid ? [uuid] : []
    updateToolbox()
  }

  function dragStarted(tool: Tool): void {
    currentToolUUIDs.value = tool.targets
    selectedTarget.value = undefined
  }

  function searchTextUpdate(search: string) {
    searchText.value = search
  }
  refreshCurrentMetric()
  async function refreshCurrentMetric() {
    currentCustomMetric.value = undefined
    const projectId = parseInt(route.params.projectId.toString())
    const metricId = parseInt(route.params.customMetricId.toString())
    if (metricId === -1) {
      openCustomMetricToEdit(undefined)
    } else {
      if (projectId && route.params.customMetricId) {
        const metric = await customMetricService.getCustomMetricsById(projectId, metricId)
        if (parseInt(route.params.projectId.toString()) == projectId && metric) {
          openCustomMetricToEdit(metric)
        }
      }
    }
  }

  async function updateToolbox() {
    const projectId = currentProject.value?.id
    if (projectId) {
      const newToolbox = await customMetricService.getToolbox(
        projectId,
        toolbox.value,
        searchText.value,
        selectedTarget.value,
      )
      if (projectId === currentProject.value?.id) {
        selectedTarget.value = undefined
        toolbox.value = newToolbox
      }
    }
  }

  async function selectTool(tool: Tool) {
    const projectId = currentProject.value?.id
    if (projectId && toolbox.value) {
      const newToolbox = await customMetricService.applyTool(projectId, toolbox.value, tool)
      if (projectId === currentProject.value?.id) {
        toolbox.value = newToolbox
      }
    }
  }

  async function droppedToolOnQuery(query: Query, tool: Tool, location: string): Promise<void> {
    const projectId = currentProject.value?.id
    if (projectId && toolbox.value) {
      const newToolbox = await customMetricService.applyTool(projectId, toolbox.value, {
        ...tool,
        targets: [location],
      })
      if (projectId === currentProject.value?.id) {
        toolbox.value = newToolbox
        selectTargetUuid(undefined)
      }
    }
  }

  EventBus.on('updateCustomMetricQueryInModal', handleUpdateQuery)
  function applyMetricName(name: string) {
    customMetricName.value = name
  }

  function applyDescription(description: string) {
    customMetricDescription.value = description
  }

  async function handleUpdateQuery(updatedQuery?: Query) {
    const projectId = currentProject.value?.id
    if (!projectId) {
      return
    }
    let newToolbox: Toolbox
    if (!updatedQuery) {
      newToolbox = await customMetricService.getInitialToolbox(projectId)
    } else {
      newToolbox = await customMetricService.getToolbox(projectId, {
        ...toolbox.value,
        query: updatedQuery,
      })
    }
    if (projectId === currentProject.value?.id) {
      const newJson = JSON.stringify(newToolbox)
        .replaceAll(/"uuid":"[^"]*"/gi, '')
        .replaceAll(/"targets":\[[^\]]*\]/gi, '')
      const oldJson = JSON.stringify(toolbox.value)
        .replaceAll(/"uuid":"[^"]*"/gi, '')
        .replaceAll(/"targets":\[[^\]]*\]/gi, '')
      if (newJson != oldJson) {
        toolbox.value = newToolbox
      }
    }
  }

  function showCreateNewCustomMetric() {
    currentCustomMetric.value = undefined
  }

  async function openCustomMetricToEdit(metric?: CustomMetric) {
    currentCustomMetric.value = metric
    customMetricName.value = metric?.name ?? ''
    customMetricDescription.value = metric?.description ?? ''
    await handleUpdateQuery(metric?.query)
  }

  async function saveCustomMetric(): Promise<void> {
    let queryToSave = query.value
    if (!currentProject?.value || !form.value || customMetricName.value == '' || !queryToSave)
      return
    if (hasToolboxAssist(queryToSave)) {
      queryToSave = removeAllToolBoxAssistFieldsFromSelect(queryToSave)
      queryToSave = removeExtraToolboxAssistFields(queryToSave)
    }
    let metric: CustomMetric
    if (currentCustomMetric.value) {
      const customMetricToUpdate: CustomMetric = {
        name: customMetricName.value,
        id: currentCustomMetric.value.id,
        description: customMetricDescription.value || '⠀⠀⠀',
        query: queryToSave,
      }

      metric = await customMetricStore.updateCustomMetric(
        currentProject.value.id,
        customMetricToUpdate,
      )
    } else {
      const newCustomMetric: Omit<CustomMetric, 'id'> = {
        name: customMetricName.value,
        description: customMetricDescription.value || '⠀⠀⠀',
        query: queryToSave,
      }
      metric = await customMetricStore.createNewCustomMetric(
        currentProject.value.id,
        newCustomMetric,
      )
    }
    currentCustomMetric.value = metric
    router.push({
      name: labels.CUSTOM_METRIC.CUSTOM_METRICS,
    })
  }

  function cancel(): void {
    router.push({ name: labels.CUSTOM_METRIC.CUSTOM_METRICS })
  }

  function toggleLayout() {
    isReversed.value = !isReversed.value
  }

  function openFilterMenu() {
    openCustomMetricFilterModal.value = true
  }
</script>
<style scoped>
  .full-width {
    width: 100%;
  }
</style>
<style scoped>
  .left-align-fields {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  .inline-header-container {
    display: inline-flex;
    align-items: center;
  }

  .edit-icon {
    margin-left: 10px;
  }

  .primary-btn {
    background-color: rgb(var(--v-theme-cyan-dark));
    color: rgb(var(--v-theme-surface));
  }

  .secondary-btn {
    background-color: rgb(var(--v-theme-itemis-hidden-grey));
    color: rgb(var(--v-theme-surface));
  }

  .flexible-container {
    display: flex;
    width: 100%;
  }

  .fixed-toolbox {
    flex: 0 0 300px;
    margin-right: 20px;
    border: 2px solid #eaeaea;
    padding: 25px;
  }

  .flexible-table {
    flex: 1;
    overflow-x: auto;
    border: 2px solid #eaeaea;
    padding: 25px;
  }

  .action-btn {
    width: 180px;
  }

  .v-container {
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    .action-btn {
      width: 100%;
      margin-top: 10px;
    }
  }
</style>
